import type { Customer } from './types';

/**
 * Customer lookup and creation.
 *
 * The search predicate lives here rather than in a screen because the cart and
 * the debt list must agree on what "matches" means — a shopkeeper who finds
 * someone on one screen and not the other will conclude the app lost them.
 */

/** Digits only, so "020 5512 3480" and "02055123480" are the same number. */
function digits(s: string): string {
  return s.replace(/\D/g, '');
}

export function matchesCustomer(customer: Customer, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  if (customer.name.toLowerCase().includes(q)) return true;
  const qd = digits(q);
  return qd.length > 0 && digits(customer.phone).includes(qd);
}

/**
 * Matching customers, best first.
 *
 * Ordered so the shop's frequent credit customers surface without typing: a
 * name that *starts* with the query beats one that merely contains it, and
 * among equals the largest outstanding balance wins — that is who the counter
 * is most likely to be ringing up on credit.
 */
export function searchCustomers(list: Customer[], query: string): Customer[] {
  const q = query.trim().toLowerCase();
  return list
    .filter((c) => matchesCustomer(c, query))
    .sort((a, b) => {
      if (q) {
        const aStarts = a.name.toLowerCase().startsWith(q) ? 0 : 1;
        const bStarts = b.name.toLowerCase().startsWith(q) ? 0 : 1;
        if (aStarts !== bStarts) return aStarts - bStarts;
      }
      return b.balance - a.balance;
    });
}

export interface NewCustomer {
  name: string;
  phone?: string;
  /** Credit ceiling in LAK. 0 or absent means no ceiling is enforced. */
  limit?: number;
}

export function newCustomer(id: string, input: NewCustomer): Customer {
  return {
    id,
    name: input.name.trim(),
    phone: input.phone?.trim() ?? '',
    balance: 0,
    limit: input.limit ?? 0,
    days: 0,
  };
}

/**
 * An existing customer with the same name, ignoring case and spacing.
 *
 * Used to warn rather than to block: two people really can share a name, but two
 * ຊ່າງ ຄຳ rows in a debt book means payments land against the wrong one, so the
 * shopkeeper should be the one deciding.
 */
export function findDuplicateName(list: Customer[], name: string): Customer | undefined {
  const key = name.trim().toLowerCase().replace(/\s+/g, ' ');
  return list.find((c) => c.name.trim().toLowerCase().replace(/\s+/g, ' ') === key);
}

/** Everyone who currently owes something. */
export function debtorCount(list: Customer[]): number {
  return list.filter((c) => c.balance > 0).length;
}
